<template>
    <div class="task-page">
        <!-- ---------- 头部 ---------- -->
        <header class="tk-hero">
            <div class="hero-top">
                <span class="hero-back" @click="goBack">‹</span>
                <span class="hero-title">作业详情</span>
                <span class="hero-back ghost"></span>
            </div>

            <div class="tk-title-row" v-if="detail">
                <span class="tk-badge" :style="{ background: detail.meta.bg, color: detail.meta.fg }">
                    {{ detail.meta.short }}
                </span>
                <div class="tk-name">
                    <p class="tk-h1">{{ detail.title }}</p>
                    <p class="tk-sub">{{ subjectText }}</p>
                </div>
                <span class="tk-status" :style="{ background: detail.statusMeta.bg, color: detail.statusMeta.fg }">
                    {{ detail.statusMeta.text }}
                </span>
            </div>
        </header>

        <main class="tk-body" v-if="detail">
            <!-- 截止信息 -->
            <section class="due-card" :class="'lv-' + detail.level">
                <div class="due-main">
                    <p class="due-label">截止时间</p>
                    <p class="due-time">{{ detail.dueAt }}</p>
                </div>
                <div class="due-side">
                    <span class="due-left">{{ detail.leftText }}</span>
                </div>
                <p class="due-remind" v-if="detail.remind">{{ detail.remind }}</p>
            </section>

            <!-- 作业要求 -->
            <section class="block">
                <div class="block-head"><span class="block-title">作业要求</span></div>
                <div class="card">
                    <p class="req" v-for="(r, i) in detail.require" :key="i">
                        <em>{{ i + 1 }}</em><span>{{ r }}</span>
                    </p>
                </div>
            </section>

            <!-- 附件 -->
            <section class="block" v-if="detail.attach && detail.attach.length">
                <div class="block-head"><span class="block-title">老师发的附件</span></div>
                <div class="card">
                    <p class="file" v-for="f in detail.attach" :key="f.name" @click="download(f)">
                        <i class="f-ico">附</i>
                        <span class="f-name">{{ f.name }}</span>
                        <span class="f-size">{{ f.size }}</span>
                    </p>
                </div>
            </section>

            <!-- 我的提交 -->
            <section class="block">
                <div class="block-head"><span class="block-title">我的提交</span></div>
                <div class="card">
                    <template v-if="detail.submit">
                        <p class="sub-time">提交于 {{ detail.submit.time }}</p>
                        <p class="file" v-for="f in detail.submit.files" :key="f.name">
                            <i class="f-ico ok">件</i>
                            <span class="f-name">{{ f.name }}</span>
                            <span class="f-size">{{ f.size }}</span>
                        </p>
                    </template>
                    <p class="empty" v-else>还没有提交，截止前记得交一下</p>
                </div>
            </section>

            <!-- 老师批改 -->
            <section class="block tk-correct" v-if="detail.submit && (detail.submit.score !== null || detail.submit.comment)">
                <div class="block-head"><span class="block-title">老师批改</span></div>
                <div class="card">
                    <div class="score-row" v-if="detail.submit.score !== null">
                        <em>{{ detail.submit.score }}</em>
                        <span class="score-full" v-if="detail.submit.full">/ {{ detail.submit.full }}</span>
                        <span class="score-teacher">{{ detail.submit.teacher }}</span>
                    </div>
                    <p class="comment">{{ detail.submit.comment }}</p>
                    <div class="fixes" v-if="detail.submit.fixes && detail.submit.fixes.length">
                        <p class="fixes-title">要订正的地方</p>
                        <p class="fix" v-for="(f, i) in detail.submit.fixes" :key="i">{{ f }}</p>
                    </div>
                </div>
            </section>

            <p class="tip">演示数据 · 接入作业表后按同样的结构返回即可</p>
        </main>

        <main class="tk-body" v-else>
            <p class="empty-lg">没找到这份作业，可能已经下架了</p>
        </main>

        <!-- ---------- 底部操作条 ---------- -->
        <div class="tk-bar" v-if="detail">
            <span class="bar-tip">{{ barTip }}</span>
            <span class="bar-btn" @click="handleAction">{{ barText }}</span>
        </div>

        <p class="toast-tip" v-if="toast">{{ toast }}</p>
    </div>
</template>

<script>
import { fetchHomeworkDetail, subjectName } from '../../utils/studyData';

const LEVEL_ACTION = {
    overdue: { text: '去补交', tip: '逾期了，尽快补上' },
    pending: { text: '去提交', tip: '截止前提交就可以' },
    correct: { text: '去订正', tip: '老师标了要订正' },
    done: { text: '查看批改', tip: '已经交过了' }
};

export default {
    name: 'AppHomeworkTask',
    data() {
        return {
            detail: null,
            toast: '',
            toastTimer: null
        };
    },
    computed: {
        subjectText() {
            if (!this.detail) {
                return '';
            }
            const teacher = this.detail.submit && this.detail.submit.teacher;
            return subjectName(this.detail.subject) + ' · ' + (teacher ? teacher + ' 布置' : '任课老师布置');
        },
        barText() {
            if (!this.detail) {
                return '';
            }
            const conf = LEVEL_ACTION[this.detail.level] || LEVEL_ACTION.pending;
            return conf.text;
        },
        barTip() {
            if (!this.detail) {
                return '';
            }
            const conf = LEVEL_ACTION[this.detail.level] || LEVEL_ACTION.pending;
            return this.detail.canSubmit ? conf.tip : '得分已出，看看错在哪';
        }
    },
    created() {
        this.load();
    },
    watch: {
        /**
         * 同组件在 /hwk/task/:id 之间跳转时 created 不会重跑，
         * 必须盯着 id 重新取数，否则会一直停在上一条作业。
         */
        '$route.params.id'() {
            this.load();
        }
    },
    beforeDestroy() {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
    },
    methods: {
        load() {
            const id = this.$route.params.id;
            // 先清空，避免切换作业时短暂显示上一条的内容
            this.detail = null;
            fetchHomeworkDetail(id)
                .then(data => {
                    this.detail = data || null;
                })
                .catch(() => {
                    this.detail = null;
                });
        },
        handleAction() {
            if (!this.detail) {
                return;
            }
            if (!this.detail.canSubmit) {
                // 已交：滚到批改区
                const el = this.$el.querySelector('.tk-correct');
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    return;
                }
                this.showToast(this.detail.submit ? '得分 ' + this.detail.submit.score + ' 分' : '已提交');
                return;
            }
            this.showToast('提交功能待接通（演示数据）');
        },
        download(file) {
            this.showToast('演示附件，暂不可下载：' + file.name);
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }
            this.$router.replace({ path: '/hwk/home' });
        },
        showToast(text) {
            this.toast = text;
            if (this.toastTimer) {
                clearTimeout(this.toastTimer);
            }
            this.toastTimer = setTimeout(() => {
                this.toast = '';
            }, 1800);
        }
    }
};
</script>

<style lang="less" scoped>
.task-page {
    min-height: 100vh;
    box-sizing: border-box;
    /* 详情页不挂底部菜单，只给操作条留位置 */
    padding-bottom: calc(170px + constant(safe-area-inset-bottom));
    padding-bottom: calc(170px + env(safe-area-inset-bottom));
    background: #f4f6fa;
}

/* ---------------- 头部 ---------------- */
.tk-hero {
    position: relative;
    padding: 40px 40px 44px;
    color: #fff;
    background: var(--blueTheme, #0066b3);
    border-radius: 0 0 44px 44px;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: -170px;
        right: -110px;
        width: 400px;
        height: 400px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.13);
    }
}

.hero-top {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.hero-back {
    width: 60px;
    font-size: 46px;
    line-height: 1;
    color: #fff;

    &.ghost {
        visibility: hidden;
    }
    &:active {
        opacity: 0.7;
    }
}

.hero-title {
    font-size: 34px;
    font-weight: 600;
    letter-spacing: 1px;
}

.tk-title-row {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    margin-top: 34px;
}

.tk-badge {
    flex: none;
    width: 64px;
    height: 64px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    font-weight: 600;
}

.tk-name {
    flex: 1;
    min-width: 0;
    margin-left: 22px;
}

.tk-h1 {
    margin: 0;
    font-size: 34px;
    font-weight: 600;
}

.tk-sub {
    margin: 10px 0 0;
    font-size: 22px;
    opacity: 0.84;
}

.tk-status {
    flex: none;
    margin-left: 16px;
    padding: 8px 20px;
    border-radius: 999px;
    font-size: 20px;
}

/* ---------------- 主体 ---------------- */
.tk-body {
    padding: 0 30px;
}

/* 截止信息 */
.due-card {
    position: relative;
    z-index: 2;
    margin-top: -26px;
    padding: 28px 30px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.08);
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 24px;
        bottom: 24px;
        width: 8px;
        border-radius: 0 8px 8px 0;
        background: #378add;
    }

    &.lv-overdue::before {
        background: #a32d2d;
    }
    &.lv-pending::before {
        background: #ef9f27;
    }
    &.lv-correct::before {
        background: #378add;
    }
    &.lv-done::before {
        background: #1d9e75;
    }
}

.due-main {
    flex: 1;
    min-width: 0;
    padding-left: 10px;
}

.due-label {
    margin: 0;
    font-size: 22px;
    color: #8a94a6;
}

.due-time {
    margin: 8px 0 0;
    font-size: 32px;
    font-weight: 600;
    color: #22283a;
}

.due-side {
    flex: none;
    margin-left: 20px;
}

.due-left {
    display: inline-block;
    padding: 8px 22px;
    border-radius: 999px;
    font-size: 22px;
    color: #5f6b7d;
    background: #f4f6fa;
}

.due-remind {
    flex: none;
    width: 100%;
    margin: 20px 0 0;
    padding-top: 18px;
    border-top: 1px solid #f2f4f8;
    font-size: 22px;
    color: #a32d2d;
}

/* 通用区块 */
.block {
    margin-top: 30px;
}

.block-head {
    display: flex;
    align-items: center;
    min-height: 56px;
    padding: 0 8px;
}

.block-title {
    font-size: 30px;
    font-weight: 600;
    color: #22283a;
}

.card {
    padding: 12px 30px;
    background: #fff;
    border-radius: 26px;
    box-shadow: 0 8px 28px rgba(15, 39, 76, 0.06);
}

.req {
    display: flex;
    margin: 0;
    padding: 22px 0;
    font-size: 26px;
    line-height: 1.7;
    color: #2b3445;

    & + .req {
        border-top: 1px solid #f2f4f8;
    }

    em {
        flex: none;
        width: 40px;
        height: 40px;
        margin: 6px 18px 0 0;
        border-radius: 12px;
        font-style: normal;
        font-size: 22px;
        font-weight: 600;
        text-align: center;
        line-height: 40px;
        color: var(--blueTheme, #0066b3);
        background: var(--lightBlueTheme, #d4eafa);
    }
}

.file {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 24px 0;
    font-size: 26px;
    color: #2b3445;

    & + .file {
        border-top: 1px solid #f2f4f8;
    }
    &:active {
        opacity: 0.8;
    }
}

.f-ico {
    flex: none;
    width: 48px;
    height: 48px;
    margin-right: 18px;
    border-radius: 14px;
    font-style: normal;
    font-size: 22px;
    line-height: 48px;
    text-align: center;
    color: #5f6b7d;
    background: #f4f6fa;

    &.ok {
        color: #0f6e56;
        background: #e1f5ee;
    }
}

.f-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.f-size {
    flex: none;
    margin-left: 16px;
    font-size: 22px;
    color: #a3abba;
}

.sub-time {
    margin: 0;
    padding: 22px 0 4px;
    font-size: 22px;
    color: #8a94a6;
}

.empty {
    margin: 0;
    padding: 46px 0;
    text-align: center;
    font-size: 24px;
    color: #a3abba;
}

/* 批改 */
.score-row {
    display: flex;
    align-items: baseline;
    padding: 24px 0 4px;

    em {
        font-style: normal;
        font-size: 56px;
        font-weight: 700;
        line-height: 1;
        color: #0f6e56;
    }
}

.score-full {
    margin-left: 10px;
    font-size: 24px;
    color: #a3abba;
}

.score-teacher {
    margin-left: auto;
    font-size: 22px;
    color: #8a94a6;
}

.comment {
    margin: 18px 0 0;
    padding: 22px 24px;
    border-radius: 18px;
    background: #f7f9fc;
    font-size: 26px;
    line-height: 1.7;
    color: #4a5566;
}

.fixes {
    margin-top: 24px;
    padding-top: 22px;
    border-top: 1px solid #f2f4f8;
}

.fixes-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #22283a;
}

.fix {
    position: relative;
    margin: 16px 0 0;
    padding-left: 24px;
    font-size: 24px;
    line-height: 1.7;
    color: #5f6b7d;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 14px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #ef9f27;
    }
}

.empty-lg {
    padding: 200px 0;
    text-align: center;
    font-size: 26px;
    color: #a3abba;
}

.tip {
    margin: 30px 0 0;
    text-align: center;
    font-size: 20px;
    color: #c2c9d6;
}

/* ---------------- 底部操作条 ---------------- */
.tk-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 320;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 112px;
    padding: 0 40px;
    padding-bottom: constant(safe-area-inset-bottom);
    padding-bottom: env(safe-area-inset-bottom);
    background: rgba(255, 255, 255, 0.97);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 -6px 24px rgba(15, 39, 76, 0.06);
}

.bar-tip {
    font-size: 22px;
    color: #8a94a6;
}

.bar-btn {
    padding: 18px 56px;
    border-radius: 999px;
    font-size: 28px;
    color: #fff;
    background: var(--blueTheme, #0066b3);

    &:active {
        opacity: 0.85;
    }
}

.toast-tip {
    position: fixed;
    left: 50%;
    bottom: calc(240px + constant(safe-area-inset-bottom));
    bottom: calc(240px + env(safe-area-inset-bottom));
    z-index: 600;
    transform: translateX(-50%);
    padding: 20px 36px;
    border-radius: 999px;
    font-size: 24px;
    color: #fff;
    background: rgba(17, 24, 39, 0.86);
    white-space: nowrap;
}
</style>
